import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useUIStore, useAuthStore, useNotificationStore, useWorkspaceStore } from '../../store';
import { api } from '../../lib/api';
import { authService } from '../../services/authService';
import { resolveLocale } from '../../lib/dateFormat';
import {
  Menu,
  User,
  LogOut,
  ChevronDown,
  LayoutDashboard,
  CheckSquare,
  AlertTriangle,
  BarChart3,
  Settings,
  Bell,
  MessageSquare
} from 'lucide-react';
import ConversationsPanel from '../chat/ConversationsPanel';
import { useConversations } from '../../hooks/useConversations';
import { useChatUnread } from '../../hooks/useChatUnread';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { toggleSidebar, theme, language } = useUIStore();
  const { user, isAuthenticated, logout } = useAuthStore();
  const { workspace, setWorkspace, myWorkspaces, setMyWorkspaces } = useWorkspaceStore();

  useEffect(() => {
    if (workspace) return;
    api.get('/workspace/me')
      .then((res) => {
        const data = (res.data as any)?.data;
        if (data) setWorkspace(data);
      })
      .catch(() => {});
  }, [workspace, setWorkspace]);

  useEffect(() => {
    if (myWorkspaces.length > 0) return;
    authService.listMyWorkspaces()
      .then(setMyWorkspaces)
      .catch(() => {});
  }, [myWorkspaces, setMyWorkspaces]);
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll } = useNotificationStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(false);
  // Unread badge: the backend has no read-tracking, so read state is kept
  // per browser (store/chatRead.ts) and the lists are polled to stay fresh.
  const { taskConversations, groupConversations, loaded: conversationsLoaded, refetch: refetchConversations } = useConversations(60000);
  const { unreadCount: conversationCount } = useChatUnread(taskConversations, groupConversations, conversationsLoaded);
  const location = useLocation();
  const navigate = useNavigate();

  // Same entity_type -> route mapping as NotificationToast.tsx's
  // handleClick — kept consistent so bell-click and toast-click behave
  // the same for every type.
  const handleNotificationClick = (n: (typeof notifications)[number]) => {
    if (!n.is_read) markAsRead(n.id);
    if (n.entity_type === 'task' && n.entity_id) {
      navigate(`/tasks/${n.entity_id}`);
      setShowNotifications(false);
    } else if (n.entity_type === 'incident' && n.entity_id) {
      navigate(`/incidents/${n.entity_id}`);
      setShowNotifications(false);
    } else if (n.entity_type === 'workspace_invitation' && n.entity_id) {
      navigate(`/accept-invite?invitationId=${n.entity_id}`);
      setShowNotifications(false);
    }
  };

  const formatNotificationDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(resolveLocale(language), {
        month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const navigation = [
    { name: t('common.dashboard'), href: '/dashboard', icon: LayoutDashboard },
    { name: t('common.tasks'), href: '/tasks', icon: CheckSquare },
    { name: t('common.incidents'), href: '/incidents', icon: AlertTriangle },
    { name: t('common.reports'), href: '/reports', icon: BarChart3 },
    { name: t('common.settings'), href: '/settings', icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  return (
    <header className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-[#76d1e3] border-[#5bc4d8]'} shadow-sm border-b h-16 flex items-center justify-between px-6`}>
      {/* Left side - brand block + mobile menu toggle. This is the
          sidebar's own top-left corner space (sidebar starts below the
          header, at top-16) — the brand lives here instead of inside
          Sidebar.tsx so it fills that region instead of leaving it blank. */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={toggleSidebar}
          className={`lg:hidden p-2 -ml-2 rounded-md transition-colors duration-200 flex-shrink-0 ${
            theme === 'dark'
              ? 'text-gray-300 hover:text-white hover:bg-gray-700'
              : 'text-white hover:text-[#e8f7fa] hover:bg-[#5bc4d8]'
          }`}
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white flex items-center justify-center p-1">
            <img src="/logo.png" alt="CEBEN" className="w-full h-full object-contain" />
          </div>
          <div className="min-w-0 hidden sm:block">
            <h1 className="text-sm font-bold leading-tight truncate text-white">CEBEN</h1>
          </div>
        </div>
      </div>

      {/* Center - Header Navigation Menu */}
      <div className="hidden lg:flex flex-1 flex justify-center">
        <nav className="flex items-center space-x-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? theme === 'dark' 
                      ? 'bg-gray-700 text-white' 
                      : 'bg-[#5bc4d8] text-white'
                    : theme === 'dark' 
                      ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                      : 'text-white hover:text-[#e8f7fa] hover:bg-[#5bc4d8]'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden lg:block">{item.name}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Right side - User account */}
      <div className="flex items-center space-x-2">
        {isAuthenticated && user && (
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className={`relative p-2 rounded-lg transition-colors duration-200 ${
                theme === 'dark'
                  ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                  : 'text-white hover:text-[#e8f7fa] hover:bg-[#5bc4d8]'
              }`}
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute top-0.5 right-0.5 flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className={`absolute right-0 mt-2 w-80 rounded-lg shadow-lg border z-50 ${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-white border-gray-200'
              }`}>
                <div className={`flex items-center justify-between px-4 py-2 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {t('notifications.notifications')}
                  </p>
                  {notifications.length > 0 && (
                    <button
                      onClick={() => markAllAsRead()}
                      className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}
                    >
                      {t('notifications.markAllAsRead')}
                    </button>
                  )}
                </div>

                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className={`px-4 py-6 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {t('notifications.noNotifications')}
                    </p>
                  ) : (
                    notifications.map((n) => (
                      <button
                        key={n.id}
                        onClick={() => handleNotificationClick(n)}
                        className={`w-full text-left px-4 py-3 border-b transition-colors duration-200 ${
                          theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
                        } ${
                          n.is_read
                            ? theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                            : theme === 'dark' ? 'bg-gray-700/60 hover:bg-gray-700' : 'bg-blue-50 hover:bg-blue-100'
                        }`}
                      >
                        <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {t(`notifications.byType.${n.type}`, { defaultValue: n.title })}
                        </p>
                        <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{n.message}</p>
                        <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>{formatNotificationDate(n.created_at)}</p>
                      </button>
                    ))
                  )}
                </div>

                {notifications.length > 0 && (
                  <div className={`px-4 py-2 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                    <button
                      onClick={() => clearAll()}
                      className={`w-full text-center text-xs font-medium ${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}
                    >
                      {t('notifications.clearAll')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {isAuthenticated && user && (
          <div className="relative">
            <button
              onClick={() => setShowChatPanel(!showChatPanel)}
              className={`relative p-2 rounded-lg transition-colors duration-200 ${
                theme === 'dark'
                  ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                  : 'text-white hover:text-[#e8f7fa] hover:bg-[#5bc4d8]'
              }`}
            >
              <MessageSquare className="h-5 w-5" />
              {conversationCount > 0 && (
                <span className="absolute top-0.5 right-0.5 flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                  {conversationCount > 99 ? '99+' : conversationCount}
                </span>
              )}
            </button>

            {showChatPanel && (
              <ConversationsPanel onClose={() => { setShowChatPanel(false); refetchConversations(); }} />
            )}
          </div>
        )}

        {isAuthenticated && user ? (
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`flex items-center space-x-2 transition-colors duration-200 ${
                theme === 'dark' 
                  ? 'text-gray-300 hover:text-white' 
                  : 'text-white hover:text-[#e8f7fa]'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                theme === 'dark' 
                  ? 'bg-gray-600 bg-opacity-50' 
                  : 'bg-white bg-opacity-20'
              }`}>
                <User className="h-4 w-4" />
              </div>
              <span className="hidden sm:block text-sm font-medium">
                {user.name}
              </span>
              <ChevronDown className="h-4 w-4" />
            </button>

            {/* User dropdown menu */}
            {showUserMenu && (
              <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg border z-50 ${
                theme === 'dark' 
                  ? 'bg-gray-800 border-gray-700' 
                  : 'bg-white border-gray-200'
              }`}>
                <div className="py-2">
                  <div className={`px-4 py-2 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.name}</p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</p>
                  </div>
                  <NavLink
                    to="/settings"
                    onClick={() => setShowUserMenu(false)}
                    className={`w-full flex items-center px-4 py-2 text-sm transition-colors duration-200 ${
                      theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <User className="h-4 w-4 mr-2" />
                    {t('settings.nav.profile')}
                  </NavLink>
                  <button
                    onClick={handleLogout}
                    className={`w-full flex items-center px-4 py-2 text-sm transition-colors duration-200 ${
                      theme === 'dark' 
                        ? 'text-gray-300 hover:bg-gray-700' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('common.logout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center space-x-3">
            <button className={`transition-colors duration-200 text-sm font-medium ${
              theme === 'dark' 
                ? 'text-gray-300 hover:text-white' 
                : 'text-white hover:text-[#e8f7fa]'
            }`}>
              {t('auth.login')}
            </button>
            <button className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
              theme === 'dark' 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-white text-[#76d1e3] hover:bg-[#e8f7fa]'
            }`}>
              {t('auth.signup')}
            </button>
          </div>
        )}
      </div>


      {/* Click outside to close user menu / notifications dropdown / chat panel */}
      {(showUserMenu || showNotifications || showChatPanel) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => { setShowUserMenu(false); setShowNotifications(false); setShowChatPanel(false); }}
        />
      )}
    </header>
  );
};

export default Header;
